import { useState, type ReactNode } from 'react';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BILL_MOVEMENT_KIND_LABELS,
  BILL_STATE_LABELS,
  SUPPLIER_TRADE_LABELS,
  billOverdue,
  formatNzDate,
  formatRef,
  nzDate,
  parseRef,
} from '@promanage/core';
import {
  Amount,
  Button,
  Chip,
  Field,
  Input,
  Panel,
  PanelHeader,
  Ref,
  Textarea,
} from '@promanage/ui';
import { RecordNotFound } from '../components/RecordNotFound';
import { TopBar } from '../components/TopBar';
import { Icon } from '../components/Icon';
import { RecordIdentity } from '../components/RecordIdentity';
import { BillInvoice } from '../features/bills/BillInvoice';
import { invalidateAccounts } from '../features/ledger/invalidate';
import type { BillDetail } from '../features/bills/types';
import { RecoverFromTenant } from '../features/tenancyInvoices/RecoverFromTenant';
import { trpc } from '../lib/trpc';

/**
 * One bill, addressed by its display ref: /bills/BL000003.
 *
 * Same ref contract as /maintenance/contractors/$ref — `beforeLoad` normalises anything
 * ref-shaped to the canonical form, and the component answers each remaining wrong shape distinctly.
 *
 * THE PAGE IS A LADDER, and it only ever offers the next rung. A captured bill offers approval; an
 * approved one offers payment; a paid one offers neither and shows the two transactions instead.
 * That is not a cosmetic simplification — the API refuses out-of-order calls anyway — it is what
 * stops the screen presenting an operator with a button whose only outcome is a refusal.
 *
 * THE TWO POSTINGS ARE SHOWN AS A HISTORY WITH DRILL-DOWN, because product principle 4 says every
 * figure reaches the transaction that caused it in one click. A reversed posting stays on that
 * list, struck through: a corrected mistake must not look like one never made.
 */
export const Route = createFileRoute('/bills/$ref')({
  beforeLoad: ({ params }) => {
    const parsed = parseRef(params.ref);
    if (parsed?.entity === 'bill') {
      const canonical = formatRef('bill', parsed.sequence);
      // Loop-safe: formatRef of a parsed canonical ref is that same string.
      if (canonical !== params.ref) {
        throw redirect({ to: '/bills/$ref', params: { ref: canonical }, replace: true });
      }
    }
  },
  component: BillDetailPage,
});

function BillDetailPage() {
  const { ref } = Route.useParams();
  const parsed = parseRef(ref);

  const record = useQuery(
    trpc.bills.get.queryOptions(
      { refSeq: parsed?.sequence ?? 0 },
      { enabled: parsed?.entity === 'bill', staleTime: 10_000 },
    ),
  );

  if (parsed === null || parsed.entity !== 'bill') {
    return (
      <NotFound
        title={parsed === null ? `“${ref}” is not a record reference` : 'That is not a bill'}
      >
        {parsed === null
          ? 'A bill reference looks like BL000003.'
          : `${ref} belongs to another kind of record. Bill references start with BL.`}
      </NotFound>
    );
  }

  if (record.isPending) {
    return <p className="px-1 py-6 text-[0.8125rem] text-ink-muted">Loading…</p>;
  }

  if (record.error || !record.data) {
    return (
      <NotFound title="No such bill">
        {`${ref} is not a bill in this agency. It may have been keyed from another agency's records.`}
      </NotFound>
    );
  }

  return <Loaded bill={record.data} />;
}

const STATE_TONE: Record<BillDetail['state'], 'neutral' | 'accent' | 'positive' | 'warn'> = {
  awaiting_approval: 'warn',
  approved: 'accent',
  paid: 'positive',
  rejected: 'neutral',
};

function Loaded({ bill }: { bill: BillDetail }) {
  const queryClient = useQueryClient();
  const today = nzDate(new Date());

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: trpc.bills.get.queryKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.bills.list.queryKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.bills.owing.queryKey() });
    // Both postings move a ledger balance, so every view of the books changed.
    invalidateAccounts(queryClient);
  };

  const amountCents = BigInt(bill.amountCents);
  const chaseable = bill.state === 'awaiting_approval' || bill.state === 'approved';
  const late = chaseable && billOverdue(bill.dueOn, today);

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title={bill.supplierName}
        subtitle={bill.description}
        actions={
          <Link to="/bills">
            <Button size="sm" variant="secondary">
              All bills
            </Button>
          </Link>
        }
      />

      <div className="flex flex-col gap-4">
        {/*
         * THE SHARED IDENTITY CARD, as on a contractor, owner or property. The bill's own version
         * put a two-column list in a box that did not grow beside the amount, so the facts
         * squeezed to their content width and wrapped against each other. The figure is the
         * title because it is what the bill IS; the supplier is already the page's own heading.
         */}
        <RecordIdentity
          title={<Amount value={amountCents} currency tone="neutral" />}
          symbol={<Icon name="invoice" className="size-6" />}
          status={
            <>
              <Ref entity="bill" sequence={bill.refSeq} />
              <Chip tone={STATE_TONE[bill.state]}>{BILL_STATE_LABELS[bill.state]}</Chip>
              {late ? <Chip tone="warn">Overdue</Chip> : null}
            </>
          }
          subtitle={
            <>
              GST inclusive, as invoiced · entered by{' '}
              {bill.capturedByName ?? <span className="text-ink-subtle">somebody unknown</span>}
            </>
          }
          facts={[
            {
              label: 'Contractor',
              value: (
                <Link
                  to="/maintenance/contractors/$ref"
                  params={{ ref: bill.supplierRef }}
                  className="hover:text-accent hover:underline"
                >
                  {bill.supplierName}
                  <span className="font-normal text-ink-muted">
                    {' '}
                    · {SUPPLIER_TRADE_LABELS[bill.supplierTrade]}
                  </span>
                </Link>
              ),
            },
            {
              label: 'Property',
              value: (
                <Link
                  to="/properties/$ref"
                  params={{ ref: bill.propertyRef }}
                  className="hover:text-accent hover:underline"
                >
                  {bill.propertyAddress}
                </Link>
              ),
            },
            {
              label: 'Invoice number',
              value: bill.invoiceNumber ?? (
                <span className="font-normal text-ink-subtle">Not numbered</span>
              ),
            },
            { label: 'Invoiced', value: formatNzDate(bill.invoiceDate) },
            {
              label: 'Due',
              value:
                bill.dueOn === null ? (
                  <span className="font-normal text-ink-subtle">No date given</span>
                ) : (
                  <span className={late ? 'text-warn' : undefined}>{formatNzDate(bill.dueOn)}</span>
                ),
            },
            {
              label: 'Maintenance job',
              value:
                bill.maintenanceJobRef === null ? (
                  // Not missing data. Most invoices are not against a job at all — a rates notice,
                  // a lawnmowing round, the annual gas certificate.
                  <span className="font-normal text-ink-subtle">Not against a job</span>
                ) : (
                  <Link
                    to="/maintenance/$ref"
                    params={{ ref: bill.maintenanceJobRef }}
                    className="hover:text-accent hover:underline"
                  >
                    {bill.maintenanceJobRef}
                  </Link>
                ),
            },
          ]}
        />

        <div className="grid gap-4 lg:grid-cols-3">
          <div className="flex min-w-0 flex-col gap-4 lg:col-span-2">
            {/*
             * FIRST IN THE MAIN COLUMN, beside the actions: approving is checking the keyed
             * figures against this paper, so the two sit side by side. See `BillInvoice`.
             */}
            <BillInvoice billId={bill.id} />

            <Movements bill={bill} />

            {/*
             * THE OTHER DIRECTION, and it belongs on this page rather than on a screen of its own.
             * The moment anybody knows the tenant is liable for this water bill is the moment they
             * are looking at the invoice; sending them elsewhere to retype the amount and the date
             * is how a recovery gets forgotten, which costs the owner the whole bill.
             *
             * IN THE MAIN COLUMN RATHER THAN BESIDE THE ACTIONS, because it is not an action on
             * the bill. Approving and paying move the agency's obligation to the SUPPLIER; this
             * raises a separate debt owed by a different person, and the column implied a sequence
             * that does not exist. It also needs the width: the question it answers first is which
             * household is liable, and at a third of a page that was a bare list of refs.
             *
             * OFFERED ON A REJECTED BILL TOO, deliberately: what the tenant owes is a fact about
             * their tenancy agreement, and it does not stop being true because the agency refused
             * to pay this particular piece of paper.
             */}
            <RecoverFromTenant bill={bill} />
          </div>

          <div className="flex flex-col gap-4">
            <Actions bill={bill} today={today} onDone={refresh} />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * What has been posted against this bill.
 *
 * REVERSED ROWS STAY, struck through and labelled. Reversing an approval genuinely returns the
 * bill to the queue — the owners' money went back — and the row that says somebody approved it
 * once is exactly what an auditor is looking for.
 */
function Movements({ bill }: { bill: BillDetail }) {
  return (
    <Panel className="p-5">
      <PanelHeader
        title="What has been posted"
        subtitle="Approval charges the owners; payment sends the money"
        className="p-0 pb-3"
      />

      {bill.movements.length === 0 ? (
        <p className="text-[0.8125rem] text-ink-muted">
          Nothing yet. Entering an invoice records it and nothing else — the owners are not charged
          and no money has left the trust account.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {bill.movements.map((movement) => (
            <li
              key={movement.id}
              className="flex flex-wrap items-baseline justify-between gap-2 border-b border-rule pb-2 last:border-0 last:pb-0"
            >
              <div className="min-w-0">
                <span
                  className={
                    movement.reversed ? 'font-medium text-ink-subtle line-through' : 'font-medium'
                  }
                >
                  {BILL_MOVEMENT_KIND_LABELS[movement.kind]}
                </span>
                <span className="text-[0.8125rem] text-ink-muted">
                  {' '}
                  {formatNzDate(movement.occurredOn)}
                </span>
                {movement.reversed ? (
                  <Chip tone="neutral" className="ml-2">
                    Reversed
                  </Chip>
                ) : null}
              </div>

              <div className="flex items-baseline gap-3">
                <Amount
                  value={BigInt(movement.amountCents)}
                  currency
                  tone={movement.reversed ? 'neutral' : 'accent'}
                />
                {/* Product principle 4: the figure reaches the transaction that caused it in
                    one click. The ledger list is searched by ref, the same drill-down an owner
                    statement uses. */}
                <Link
                  to="/financial/transactions"
                  search={{ q: movement.transactionRef }}
                  className="text-[0.75rem] tabular-nums text-ink-muted hover:text-accent hover:underline"
                >
                  {movement.transactionRef}
                </Link>
              </div>
            </li>
          ))}
        </ul>
      )}

      {bill.state === 'rejected' ? (
        <div className="mt-4 rounded-md bg-surface-sunk px-3 py-2">
          <p className="text-[0.75rem] font-semibold text-ink">
            Rejected{bill.rejectedByName ? ` by ${bill.rejectedByName}` : ''}
          </p>
          <p className="mt-1 text-[0.8125rem] text-ink-muted">{bill.rejectedReason}</p>
        </div>
      ) : null}
    </Panel>
  );
}

/**
 * The next rung, and only the next one.
 *
 * Each of these is a different authority — the property book keys and refuses an invoice, a trust
 * role approves it, a `trust_admin` pays it — and the API enforces that, not this component. What
 * the component does is refuse to show a control whose only possible outcome is a refusal.
 */
function Actions({ bill, today, onDone }: { bill: BillDetail; today: string; onDone: () => void }) {
  const [approvedOn, setApprovedOn] = useState(today);
  const [paidOn, setPaidOn] = useState(today);
  const [reason, setReason] = useState('');

  const approve = useMutation(
    trpc.bills.approve.mutationOptions({
      meta: { errorTitle: 'Could not approve that bill' },
      onSuccess: onDone,
    }),
  );
  const pay = useMutation(
    trpc.bills.pay.mutationOptions({
      meta: { errorTitle: 'Could not pay that bill' },
      onSuccess: onDone,
    }),
  );
  const reject = useMutation(
    trpc.bills.reject.mutationOptions({
      meta: { errorTitle: 'Could not reject that bill' },
      onSuccess() {
        setReason('');
        onDone();
      },
    }),
  );

  if (bill.state === 'rejected') {
    return (
      <Panel className="p-5">
        <PanelHeader title="Nothing to do" className="p-0 pb-3" />
        <p className="text-[0.8125rem] text-ink-muted">
          This invoice was refused and will not be paid. A bill cannot be revived — if it turns out
          to be payable after all, enter it again.
        </p>
      </Panel>
    );
  }

  if (bill.state === 'paid') {
    return (
      <Panel className="p-5">
        <PanelHeader title="Settled" className="p-0 pb-3" />
        <p className="text-[0.8125rem] text-ink-muted">
          The owners were charged when this was approved and the money has left the trust account. A
          payment made in error is corrected by reversing the transaction, which puts the bill back
          where it was rather than editing what happened.
        </p>
      </Panel>
    );
  }

  if (bill.state === 'approved') {
    return (
      <Panel className="p-5">
        <PanelHeader
          title="Pay it"
          subtitle="Trust administrator, with recent MFA"
          className="p-0 pb-3"
        />
        <p className="text-[0.8125rem] text-ink-muted">
          The owners have already been charged{' '}
          <Amount value={BigInt(bill.approvedCents)} currency tone="neutral" /> — this sends it to{' '}
          {bill.supplierName}’s account on file. A bank account recorded in the last 24 hours is
          held and will refuse this.
        </p>

        <div className="mt-3 flex flex-col gap-3">
          <Field label="Paid on">
            {(props) => (
              <Input
                {...props}
                type="date"
                value={paidOn}
                onChange={(e) => setPaidOn(e.target.value)}
              />
            )}
          </Field>

          <Button
            onClick={() => pay.mutate({ billId: bill.id, paidOn })}
            disabled={pay.isPending || paidOn === ''}
          >
            {pay.isPending ? (
              'Paying'
            ) : (
              <>
                Pay <Amount value={BigInt(bill.amountCents)} currency tone="neutral" />
              </>
            )}
          </Button>
        </div>
      </Panel>
    );
  }

  return (
    <>
      <Panel className="p-5">
        <PanelHeader
          title="Approve it"
          subtitle="Trust role, with recent MFA"
          className="p-0 pb-3"
        />
        <p className="text-[0.8125rem] text-ink-muted">
          This charges <Amount value={BigInt(bill.amountCents)} currency tone="neutral" /> to the
          owners of {bill.propertyAddress}, divided by whoever owned it on{' '}
          {formatNzDate(bill.invoiceDate)}. Their money stops being available to them from that
          moment — which is what keeps the next payment run from paying it away.
        </p>

        <div className="mt-3 flex flex-col gap-3">
          <Field label="Approved on" hint="Cannot be before the invoice date">
            {(props) => (
              <Input
                {...props}
                type="date"
                min={bill.invoiceDate}
                value={approvedOn}
                onChange={(e) => setApprovedOn(e.target.value)}
              />
            )}
          </Field>

          <Button
            onClick={() => approve.mutate({ billId: bill.id, approvedOn })}
            disabled={approve.isPending || approvedOn === ''}
          >
            {approve.isPending ? 'Approving' : 'Approve'}
          </Button>
        </div>
      </Panel>

      <Panel className="p-5">
        <PanelHeader
          title="Or refuse it"
          subtitle="A duplicate, a job never done, the wrong agency"
          className="p-0 pb-3"
        />
        <p className="text-[0.8125rem] text-ink-muted">
          Nothing is posted. The reason stays on the record — it is what the contractor is told and
          what answers an owner asking why their tradesperson was never paid.
        </p>

        <div className="mt-3 flex flex-col gap-3">
          <Field label="Reason" required>
            {(props) => (
              <Textarea
                {...props}
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Duplicate of BL000004 — already paid"
              />
            )}
          </Field>

          <Button
            variant="secondary"
            onClick={() => reject.mutate({ billId: bill.id, reason: reason.trim() })}
            disabled={reject.isPending || reason.trim().length < 10}
          >
            {reject.isPending ? 'Rejecting' : 'Reject invoice'}
          </Button>
        </div>
      </Panel>
    </>
  );
}

function NotFound({ title, children }: { title: string; children: ReactNode }) {
  return (
    <RecordNotFound
      record="Bill"
      title={title}
      back={{ label: 'Back to bills', link: { to: '/bills' } }}
    >
      {children}
    </RecordNotFound>
  );
}
